import { ApprovalRounded, CompareRounded } from '@mui/icons-material'
import { IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { IApprovalRequest } from '../../definitions/Approval'
import Helper from '../../helpers/Helper'
import ApprovalManager from '../../managers/ApprovalManager'
import SmartCard from '../controls/cards/SmartCard'
import KPICard from '../controls/data_visualization/KPICard'
import { Cell, Column, Row, SmartTable } from '../controls/table'
import Tile from '../layout/Tile'
import { changeRoute } from '../../store/slices/SessionSlice'
import { useAppDispatch } from '../../store/Hooks'
import Formatter from '../../helpers/Formatter'

interface P {
    onManageApproval: (item: IApprovalRequest) => void
    onRowClick: (indexRow: number, cells: Cell[], row: Row, item: IApprovalRequest) => void
    approvalData: IApprovalRequest[]
    isLoading?: string
}

const ApprovalTable = ({ onManageApproval, onRowClick, approvalData, isLoading }: P) => {
    const theme = useTheme()
    const dispatch = useAppDispatch()

    const loading = isLoading === 'loading'
    const approvals = approvalData ?? []

    const readyForReviewItems = approvals.filter(item => item.status === 'ready-for-review')
    const approvedItems = approvals.filter(item => item.status === 'approved')

    const columns: Column<IApprovalRequest>[] = [
        { id: 'uuid', label: 'UUID', isPK: true, hidden: true },
        {
            id: 'entityName',
            label: 'Name',
            customRenderer: (cell, row, item) => (
                <span
                    style={{
                        cursor: 'pointer'
                    }}
                    onClick={e => {
                        e.stopPropagation()
                        const rowIndex = row._index ?? 0
                        const cells = row.cells ?? []
                        onRowClick(rowIndex, cells, row, item)
                    }}>
                    {cell.value}
                </span>
            )
        },
        {
            id: 'status',
            label: 'Status',
            customRenderer: cell => ApprovalManager.getStatusChip(cell.value as string)
        },
        { id: 'entityType', label: 'Type' },
        {
            id: 'changes.userName',
            label: 'Requested By',
            customRenderer: cell => {
                if (!cell.value || cell.value === 'null' || cell.value === null) {
                    return <span style={{ color: theme.palette.text.secondary }}>NOT REQUESTED</span>
                }

                if (Array.isArray(cell.value) && cell.value.length > 0) {
                    return <span>{cell.value[cell.value.length - 2]}</span>
                }

                return <span>{cell.value}</span>
            }
        },
        {
            id: 'action',
            label: 'Last update',
            customRenderer: (cell, row, item) => <Typography>{Formatter.truncateIsoDate(item.changes[item.changes.length - 1].changedAt)}</Typography>
        },
        {
            id: 'action',
            label: 'Approval',
            customRenderer: (cell, row, item) => (
                <Stack flexDirection='row' gap={2}>
                    <Tooltip arrow title='Manage the approval'>
                        <IconButton
                            size='small'
                            onClick={e => {
                                e.stopPropagation()
                                onManageApproval(item)
                            }}>
                            <ApprovalRounded fontSize='small' />
                        </IconButton>
                    </Tooltip>
                    <Tooltip arrow title={!item?.originalEntityUUID ? 'Live is not available to be compared' : 'Compare entity with its live.'}>
                        <span>
                            <IconButton
                                size='small'
                                disabled={!item?.originalEntityUUID}
                                onClick={e => {
                                    e.stopPropagation()
                                    dispatch(
                                        changeRoute({
                                            route: 'approval-audit',
                                            params: {
                                                entityName: item.entityName,
                                                entityType: item.entityType,
                                                approvalEntityUUID: item.approvalEntityUUID,
                                                originalEntityUUID: item.originalEntityUUID,
                                                approvalType: 'admin'
                                            }
                                        })
                                    )
                                }}>
                                <CompareRounded fontSize='small' />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            )
        }
    ]

    return (
        <>
            <Tile.Row flex={0} spacing={2} style={{ justifyContent: 'flex-start' }}>
                <SmartCard flex={0.25} style={{ borderColor: theme.palette.primary.main }}>
                    <KPICard
                        data={Helper.dataframeToKPICard({ points: [{ x: 'current', y: readyForReviewItems.length }], delta: 0, deltaPercentage: 0 })}
                        inverse
                        label='READY FOR REVIEW'
                        isLoading={loading}
                    />
                </SmartCard>
                <SmartCard flex={0.25} style={{ borderColor: theme.palette.success.main }}>
                    <KPICard
                        data={Helper.dataframeToKPICard({ points: [{ x: 'current', y: approvedItems.length }], delta: 0, deltaPercentage: 0 })}
                        inverse
                        label='APPROVED'
                        isLoading={loading}
                    />
                </SmartCard>
            </Tile.Row>
            <SmartCard table={true}>
                <SmartTable<IApprovalRequest>
                    hidePagination
                    columns={columns}
                    noCollapse
                    datasource={{ items: approvals }}
                    toolbar={{
                        columns: true,
                        search: ['entityName', 'entityType', 'changes.changedBy', 'status'],
                        filters: true,
                        export: {
                            fileName: 'Approvals_table'
                        }
                    }}
                />
            </SmartCard>
        </>
    )
}

export default ApprovalTable
